import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Text, SimpleGrid, VStack, Tabs, TabList, Tab, TabPanels, TabPanel,
  Flex, Tooltip, Input, Badge, useToast, Spinner,
} from '@chakra-ui/react'
import { useDraggable } from '@dnd-kit/core'
import {
  Type, AlignLeft, Image, MousePointer, Minus, ArrowUpDown,
  Share2, Mail, Code2, Columns2, PanelLeft, Video, Plus, Copy,
  User, Trash2, Star, RefreshCw, LayoutGrid, PlayCircle, ClipboardList, Clock,
} from 'lucide-react'
import { CONTENT_BLOCK_LIST, LAYOUT_BLOCK_LIST, VARIABLES, BLOCK_LABELS } from './blockDefaults'
import useEmailDesignerStore from './emailDesignerStore'
import { getActiveField } from './activeFieldTracker'
import { listUniversalBlocks, deleteUniversalBlock } from './universalBlocksClient'
import { PREBUILT_CATEGORIES, PREBUILT_HEADERS, PREBUILT_FOOTERS } from './prebuilt'
import { blocksToHtml, decodeBlocksContent } from './htmlExport'
import { PRIMARY_BG } from '../../../../theme/brand';

// Settings used only to render Pre-built thumbnail previews.
const PREVIEW_SETTINGS = {
  backgroundColor: '#F7F9FC', backgroundType: 'solid',
  contentBackground: '#ffffff', contentBackgroundType: 'solid',
  contentWidth: '600', fontFamily: 'Arial, Helvetica, sans-serif',
  linkColor: '#2563eb', preheaderText: '',
}

// Renders a live, scaled-down thumbnail of a template's real exported HTML.
function TemplatePreview({ tpl }) {
  const html = useMemo(() => { try { return blocksToHtml(tpl.blocks, PREVIEW_SETTINGS) } catch { return '' } }, [tpl])
  return (
    <Box position="relative" w="100%" h="168px" overflow="hidden" bg="white" borderTopRadius="9px">
      <Box as="iframe" title={tpl.name} srcDoc={html} scrolling="no" tabIndex={-1} aria-hidden="true"
        style={{ position: 'absolute', top: 0, left: 0, width: '600px', height: '390px', border: 0,
          transformOrigin: 'top left', transform: 'scale(0.435)', pointerEvents: 'none' }} />
    </Box>
  )
}

const ICONS = {
  heading: Type, text: AlignLeft, image: Image, button: MousePointer,
  video: Video, divider: Minus, spacer: ArrowUpDown,
  social: Share2, footer: Mail, html: Code2, columns: Columns2,
  headerBar: PanelLeft, dropShadow: LayoutGrid, story: PlayCircle, ampForm: ClipboardList,
  countdown: Clock,
}

const CATEGORY_ICONS = { customer: User }
const CATEGORY_COLORS = { customer: PRIMARY_BG }

/* ─── Draggable content block card ─── */
function DraggableBlock({ item }) {
  const { addBlock } = useEmailDesignerStore()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar::${item.type}::${item.label}`,
    data: { source: 'sidebar', blockType: item.type, propsOverride: item.propsOverride || {} },
  })
  const Icon = ICONS[item.type] || PanelLeft

  return (
    <Tooltip label={`Add ${item.label}`} placement="right" hasArrow openDelay={600}>
      <Box
        ref={setNodeRef} {...attributes} {...listeners}
        onClick={() => addBlock(item.type, -1, item.propsOverride || {})}
        opacity={isDragging ? 0.35 : 1}
        p="10px 8px"
        borderRadius="10px"
        bg="white"
        border="1.5px solid" borderColor={item.color + '22'}
        _hover={{
          bg: `${item.color}08`,
          borderColor: `${item.color}44`,
          transform: 'translateY(-2px)',
          shadow: `0 4px 14px ${item.color}18`,
        }}
        transition="all 0.15s ease"
        textAlign="center"
        cursor="grab"
        userSelect="none"
        _active={{ cursor: 'grabbing', transform: 'scale(0.96)' }}
        shadow="0 1px 3px rgba(0,0,0,0.04)"
      >
        <Flex justify="center" mb="7px">
          <Box
            w="38px" h="38px" borderRadius="10px"
            bg={`${item.color}10`} border={`1.5px solid ${item.color}22`}
            display="flex" alignItems="center" justifyContent="center"
          >
            <Icon size={16} color={item.color} strokeWidth={2} />
          </Box>
        </Flex>
        <Text fontSize="10px" color="gray.600" fontWeight="700" lineHeight="1.2" noOfLines={1}>
          {item.label}
        </Text>
      </Box>
    </Tooltip>
  )
}

/* ─── Layout block row ─── */
function LayoutBlockRow({ item }) {
  const { addBlock } = useEmailDesignerStore()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar::${item.type}::${item.label}`,
    data: { source: 'sidebar', blockType: item.type, propsOverride: item.propsOverride || {} },
  })
  const cols = item.propsOverride?.columns || 1

  return (
    <Box
      ref={setNodeRef} {...attributes} {...listeners}
      onClick={() => addBlock(item.type, -1, item.propsOverride || {})}
      opacity={isDragging ? 0.35 : 1}
      p="11px 14px"
      borderRadius="10px"
      bg="white"
      border="1.5px solid" borderColor={item.color + '22'}
      _hover={{ bg: `${item.color}08`, borderColor: `${item.color}44`, transform: 'translateY(-1px)', shadow: `0 4px 14px ${item.color}18` }}
      transition="all 0.15s ease"
      cursor="grab"
      userSelect="none"
      shadow="0 1px 3px rgba(0,0,0,0.04)"
    >
      <Flex align="center" gap={3}>
        <Flex gap="3px" flexShrink={0}>
          {Array.from({ length: cols }, (_, i) => (
            <Box
              key={i}
              h="26px"
              w={cols === 1 ? '48px' : cols === 2 ? '22px' : '14px'}
              borderRadius="4px"
              bg={item.color + '10'} border={`1.5px solid ${item.color}33`}
            />
          ))}
        </Flex>
        <Box>
          <Text fontSize="12px" color="gray.700" fontWeight="700">{item.label}</Text>
          <Text fontSize="10px" color="gray.400" mt="1px">
            {cols === 1 ? 'Full width' : `${cols} equal columns`}
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

/* ─── Variable row ─── */
function VariableRow({ variable, onInsert, onCopy, onDelete }) {
  const [hovered, setHovered] = useState(false)
  return (
    <Flex
      align="center" justify="space-between"
      px={2.5} py={2}
      borderRadius="8px"
      bg="gray.50"
      border="1px solid" borderColor="gray.100"
      _hover={{ bg: 'white', borderColor: 'gray.200', shadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      transition="all 0.12s"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box flex={1} minW={0}>
        <Text fontSize="11.5px" fontWeight="600" color="gray.700" noOfLines={1}>{variable.label}</Text>
        <Text fontSize="10px" color="var(--brand-primary)" fontFamily="mono" fontWeight="600" mt="1px">{variable.token}</Text>
      </Box>
      <Flex gap={1} opacity={hovered ? 1 : 0} transition="opacity 0.12s">
        <Tooltip label="Insert into block" hasArrow placement="top">
          <Box
            w="24px" h="24px" borderRadius="5px"
            bg="rgb(var(--brand-primary-rgb) / 0.063)" border="1px solid rgb(var(--brand-primary-rgb) / 0.133)"
            display="flex" alignItems="center" justifyContent="center"
            cursor="pointer"
            _hover={{ bg: 'rgb(var(--brand-primary-rgb) / 0.125)' }}
            onClick={onInsert}
          >
            <Plus size={11} color={PRIMARY_BG} />
          </Box>
        </Tooltip>
        <Tooltip label="Copy token" hasArrow placement="top">
          <Box
            w="24px" h="24px" borderRadius="5px"
            bg="gray.100" border="1px solid" borderColor="gray.200"
            display="flex" alignItems="center" justifyContent="center"
            cursor="pointer"
            _hover={{ bg: 'gray.200' }}
            onClick={onCopy}
          >
            <Copy size={11} color="#6b7280" />
          </Box>
        </Tooltip>
        {onDelete && (
          <Tooltip label="Remove variable" hasArrow placement="top">
            <Box
              w="24px" h="24px" borderRadius="5px"
              bg="red.50" border="1px solid" borderColor="red.100"
              display="flex" alignItems="center" justifyContent="center"
              cursor="pointer"
              _hover={{ bg: 'red.100' }}
              onClick={onDelete}
            >
              <Trash2 size={11} color="#e53e3e" />
            </Box>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  )
}

/* ─── Main sidebar ─── */
/* ─── Draggable saved "universal" block ─── */
function DraggableUniversal({ item, onDeleted }) {
  const { addBlock } = useEmailDesignerStore()
  const toast = useToast()
  const type = item?.data?.type
  // Inserting a universal carries a reference (_universalId) so the placed block
  // stays linked to the saved original — edits to the original resolve back in.
  //
  // ET_039: props come back from the API with their HTML entity-escaped (the same
  // backend behaviour decodeBlocksContent exists for), so a reused Text/Footer block
  // showed literal <p>/<strong> tags. Decode through the shared helper — it also
  // walks nested column blocks — before handing the props to the canvas.
  const decodedProps = decodeBlocksContent([{ type, props: item?.data?.props || {} }])[0]?.props || {}
  const override = { ...decodedProps, _universalId: item.id, _universalName: item.name }
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `universal::${item.id}`,
    data: { source: 'sidebar', blockType: type, propsOverride: override },
  })
  const Icon = ICONS[type] || PanelLeft
  const UACCENT = '#0d9488'

  async function handleDelete(e) {
    e.stopPropagation()
    try {
      await deleteUniversalBlock(item.id)
      toast({ title: 'Removed from Universal', status: 'success', duration: 1500 })
      onDeleted?.(item.id)
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 3000 })
    }
  }

  return (
    <Box
      ref={setNodeRef} {...attributes} {...listeners}
      onClick={() => addBlock(type, -1, override)}
      opacity={isDragging ? 0.35 : 1}
      position="relative" p="10px 10px" borderRadius="10px" bg="white"
      border="1.5px solid" borderColor={`${UACCENT}22`}
      _hover={{ bg: `${UACCENT}08`, borderColor: `${UACCENT}55`, shadow: `0 4px 14px ${UACCENT}18`, '& .u-del': { opacity: 1 } }}
      transition="all 0.15s ease" cursor="grab" userSelect="none"
      _active={{ cursor: 'grabbing', transform: 'scale(0.98)' }}
      shadow="0 1px 3px rgba(0,0,0,0.04)"
    >
      <Flex align="center" gap={2.5}>
        <Box w="34px" h="34px" borderRadius="9px" flexShrink={0}
          bg={`${UACCENT}12`} border={`1.5px solid ${UACCENT}22`}
          display="flex" alignItems="center" justifyContent="center">
          <Icon size={15} color={UACCENT} strokeWidth={2} />
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="11.5px" fontWeight="700" color="gray.700" noOfLines={1}>{item.name}</Text>
          <Flex align="center" gap={1} mt="1px">
            <Star size={9} color={UACCENT} fill={UACCENT} />
            <Text fontSize="9.5px" color="gray.400" fontWeight="600">
              Universal · {BLOCK_LABELS[type] || type}
            </Text>
          </Flex>
        </Box>
        <Tooltip label="Remove from Universal" placement="top" hasArrow openDelay={400}>
          <Box className="u-del" opacity={0} transition="opacity 0.12s" color="gray.300"
            _hover={{ color: 'red.500' }} onClick={handleDelete} p={1}>
            <Trash2 size={13} />
          </Box>
        </Tooltip>
      </Flex>
    </Box>
  )
}

/* ─── Universal (saved reusable blocks) tab panel ─── */
function UniversalPanel() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [search, setSearch] = useState('')
  const bump = useEmailDesignerStore(s => s.universalRefreshToken)

  const load = useCallback(async () => {
    setLoading(true); setErr('')
    try { setItems(await listUniversalBlocks()) }
    catch (e) { setErr(e.message || 'Failed to load saved content') }
    finally { setLoading(false) }
  }, [])

  // Reload on mount and whenever a block is saved as universal elsewhere.
  useEffect(() => { load() }, [load, bump])

  const q = search.toLowerCase()
  const filtered = (search ? items.filter(i => (i.name || '').toLowerCase().includes(q)) : items)
    .filter(i => (i.kind || 'block') === 'block' && i.data?.type)

  return (
    <VStack gap={3} align="stretch">
      <Flex gap={2}>
        <Input size="sm" placeholder="Search saved blocks…" value={search}
          onChange={e => setSearch(e.target.value)} borderRadius="8px" fontSize="12px" />
        <Tooltip label="Refresh" hasArrow>
          <Box as="button" type="button" onClick={load} px={2} borderRadius="8px" border="1px solid"
            borderColor="gray.200" color="gray.500" _hover={{ color: '#0d9488', borderColor: '#0d9488' }}
            display="flex" alignItems="center">
            <RefreshCw size={13} />
          </Box>
        </Tooltip>
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}><Spinner size="sm" color="#0d9488" /></Flex>
      ) : err ? (
        <Text fontSize="11px" color="red.400" textAlign="center" py={6}>{err}</Text>
      ) : filtered.length === 0 ? (
        <VStack py={10} gap={2} color="gray.400">
          <LayoutGrid size={26} strokeWidth={1.5} />
          <Text fontSize="11.5px" fontWeight="700" color="gray.500">No universal blocks yet</Text>
          <Text fontSize="10.5px" textAlign="center" px={4} lineHeight="1.5">
            Select any block on the canvas and click the <b>★ Save as universal</b> icon to reuse it across emails.
          </Text>
        </VStack>
      ) : (
        <VStack gap={2} align="stretch">
          {filtered.map(item => (
            <DraggableUniversal key={item.id} item={item}
              onDeleted={id => setItems(prev => prev.filter(x => x.id !== id))} />
          ))}
        </VStack>
      )}
    </VStack>
  )
}

/* ─── Pre-built templates + header/footer styles ─── */
function PrebuiltStyleRow({ name, onClick }) {
  return (
    <Box as="button" type="button" onClick={onClick} w="100%" textAlign="left"
      p="9px 11px" borderRadius="9px" bg="white" border="1px solid" borderColor="gray.200"
      fontSize="12px" fontWeight="600" color="gray.700"
      _hover={{ borderColor: 'var(--brand-primary)', bg: 'rgb(var(--brand-primary-rgb) / 0.031)', color: 'var(--brand-primary)' }} transition="all 0.12s">
      {name}
    </Box>
  )
}

function PrebuiltPanel() {
  const { loadTemplate, blocks } = useEmailDesignerStore()
  const toast = useToast()
  const [cat, setCat] = useState(PREBUILT_CATEGORIES[0].key)
  const active = PREBUILT_CATEGORIES.find(c => c.key === cat) || PREBUILT_CATEGORIES[0]

  const applyTemplate = (tpl) => {
    if (blocks.length > 0 && !window.confirm('Replace the current design with this template?')) return
    loadTemplate(tpl.blocks, 'replace')
    toast({ title: `Loaded “${tpl.name}”`, status: 'success', duration: 1600 })
  }
  const insertGroup = (grp, mode) => {
    loadTemplate(grp.blocks, mode)
    toast({ title: `Added “${grp.name}”`, status: 'success', duration: 1400 })
  }

  return (
    <VStack gap={5} align="stretch">
      {/* Industry chips */}
      <Flex gap={1.5} wrap="wrap">
        {PREBUILT_CATEGORIES.map(c => (
          <Box as="button" type="button" key={c.key} onClick={() => setCat(c.key)}
            px={2.5} py={1} borderRadius="full" fontSize="11px" fontWeight="700" border="1px solid"
            borderColor={cat === c.key ? 'var(--brand-primary)' : 'gray.200'}
            bg={cat === c.key ? 'var(--brand-primary)' : 'white'} color={cat === c.key ? 'white' : 'gray.600'}
            _hover={{ borderColor: 'var(--brand-primary)' }} transition="all 0.12s">
            {c.label}
          </Box>
        ))}
      </Flex>

      {/* Templates for the selected industry */}
      <Box>
        <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>
          {active.label} templates
        </Text>
        {(!active.templates || active.templates.length === 0) ? (
          <Text fontSize="11px" color="gray.400" py={3}>Templates coming soon for {active.label}.</Text>
        ) : (
          <VStack gap={2} align="stretch">
            {active.templates.map(t => (
              <Box key={t.id} as="button" type="button" onClick={() => applyTemplate(t)}
                w="100%" textAlign="left" borderRadius="10px" overflow="hidden" bg="white"
                border="1.5px solid" borderColor="gray.200"
                _hover={{ borderColor: 'var(--brand-primary)', shadow: '0 6px 18px rgb(var(--brand-primary-rgb) / 0.16)' }}
                transition="all 0.14s" role="group">
                <TemplatePreview tpl={t} />
                <Box px="10px" py="8px" borderTop="1px solid" borderColor="gray.100"
                  _groupHover={{ bg: 'rgb(var(--brand-primary-rgb) / 0.031)' }}>
                  <Text fontSize="12px" fontWeight="700" color="gray.800" noOfLines={1}>{t.name}</Text>
                  {t.description && <Text fontSize="10px" color="gray.400" noOfLines={1}>{t.description}</Text>}
                </Box>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      {/* Header styles */}
      <Box>
        <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>Header styles</Text>
        <VStack gap={2} align="stretch">
          {PREBUILT_HEADERS.map(h => <PrebuiltStyleRow key={h.id} name={h.name} onClick={() => insertGroup(h, 'top')} />)}
        </VStack>
      </Box>

      {/* Footer styles */}
      <Box>
        <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>Footer styles</Text>
        <VStack gap={2} align="stretch">
          {PREBUILT_FOOTERS.map(f => <PrebuiltStyleRow key={f.id} name={f.name} onClick={() => insertGroup(f, 'bottom')} />)}
        </VStack>
      </Box>
    </VStack>
  )
}

function BlocksSidebar() {
  const [varSearch, setVarSearch] = useState('')
  const [newVarName, setNewVarName] = useState('')
  const toast = useToast()
  const { blocks, selectedBlockId, updateBlock, customVariables, addCustomVariable, removeCustomVariable } = useEmailDesignerStore()

  function handleInsertVariable(token) {
    let selBlock = blocks.find(b => b.id === selectedBlockId)
    if (!selBlock) {
      for (const b of blocks) {
        if (b.type === 'columns' && b.props?.columnDefs) {
          for (const col of b.props.columnDefs) {
            const found = col.blocks?.find(sb => sb.id === selectedBlockId)
            if (found) { selBlock = found; break }
          }
        }
        if (selBlock) break
      }
    }
    // ET_007: 'html' was missing, so a Custom HTML block could not take a variable at
    // all — the button just warned. Its panel textarea registers itself the same way
    // the Heading input does, so it goes down the caret path below.
    if (selBlock && ['text', 'heading', 'html'].includes(selBlock.type)) {
      const current = selBlock.props?.content || ''
      const active = getActiveField()?.blockId === selBlock.id ? getActiveField() : null

      // Rich-text (Quill) blocks: contentEditable has no selectionStart, which is why
      // text blocks always appended however the caret was placed. They hand us an
      // insert callback that splices at their own remembered caret instead.
      if (active?.kind === 'editor') {
        active.insert(token)
        toast({ title: 'Variable inserted', status: 'success', duration: 1500 })
        return
      }

      const el = active?.kind === 'input' ? active.element : null
      if (el && document.body.contains(el) && typeof el.selectionStart === 'number') {
        const start = el.selectionStart
        const end = el.selectionEnd
        const newContent = current.slice(0, start) + token + current.slice(end)
        updateBlock(selBlock.id, { content: newContent })
        const caretPos = start + token.length
        requestAnimationFrame(() => {
          el.focus()
          el.setSelectionRange(caretPos, caretPos)
        })
      } else {
        updateBlock(selBlock.id, { content: current + ' ' + token })
      }
      toast({ title: 'Variable inserted', status: 'success', duration: 1500 })
    } else {
      toast({ title: 'Select a Text, Heading or HTML block first', status: 'warning', duration: 2000 })
    }
  }

  function handleCopyVariable(token) {
    navigator.clipboard.writeText(token)
    toast({ title: 'Copied!', status: 'success', duration: 1200 })
  }

  function handleAddCustomVariable() {
    const name = newVarName.trim().replace(/^\$/, '').replace(/\s+/g, '_')
    if (!name) return
    const token = `$${name}`
    const alreadyExists =
      VARIABLES.some(v => v.token === token) ||
      customVariables.some(v => v.token === token)
    if (alreadyExists) {
      toast({ title: 'Variable already exists', status: 'warning', duration: 1800 })
      return
    }
    addCustomVariable({
      id: `custom_${Date.now()}`,
      label: name,
      token,
      category: 'custom',
    })
    setNewVarName('')
    toast({ title: `$${name} added`, status: 'success', duration: 1500 })
  }

  const q = varSearch.toLowerCase()
  const filteredBuiltin = varSearch
    ? VARIABLES.filter(v => v.label.toLowerCase().includes(q) || v.token.toLowerCase().includes(q))
    : VARIABLES
  const filteredCustom = varSearch
    ? customVariables.filter(v => v.label.toLowerCase().includes(q) || v.token.toLowerCase().includes(q))
    : customVariables

  const byCategory = {
    customer: filteredBuiltin.filter(v => v.category === 'customer'),
  }

  return (
    <Box
      w="300px" minW="300px" h="100%"
      bg="white"
      borderRight="1px solid" borderColor="gray.200"
      overflowY="auto"
      display="flex" flexDir="column"
      css={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}
    >
      <Tabs variant="unstyled" size="sm" flex={1} display="flex" flexDir="column">
        {/* Tab bar */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.200" px={3} pt={3} pb={0} flexShrink={0}>
          <TabList gap="2px">
            {['Blocks', 'Var', 'Pre-built', 'Universal'].map(label => (
              <Tab
                key={label}
                px={3} py={2}
                fontSize="11.5px" fontWeight="700"
                color="gray.400"
                borderRadius="8px 8px 0 0"
                mb="-1px"
                borderBottom="2px solid transparent"
                _selected={{
                  color: 'var(--brand-primary)',
                  borderBottomColor: 'var(--brand-primary)',
                  bg: 'gray.50',
                }}
                _hover={{ color: 'gray.600' }}
                transition="all 0.12s"
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </Box>

        {/* Content Blocks tab */}
        <TabPanels flex={1} overflow="hidden">
          <TabPanel px={3} pt={3} pb={4} h="100%" overflowY="auto"
            css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' } }}
          >
            <VStack gap={5} align="stretch">
              {/* Content blocks */}
              <Box>
                <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>
                  Content Blocks
                </Text>
                <SimpleGrid columns={2} gap={2}>
                  {CONTENT_BLOCK_LIST.map(item => (
                    <DraggableBlock key={item.label} item={item} />
                  ))}
                </SimpleGrid>
              </Box>

              {/* Layout blocks */}
              <Box>
                <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>
                  Layout Blocks
                </Text>
                <VStack gap={2} align="stretch">
                  {LAYOUT_BLOCK_LIST.map(item => (
                    <LayoutBlockRow key={item.label} item={item} />
                  ))}
                </VStack>
              </Box>
            </VStack>
          </TabPanel>

          {/* Variables tab */}
          <TabPanel px={3} pt={3} pb={4} h="100%" overflowY="auto"
            css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' } }}
          >
            <VStack gap={4} align="stretch">
              {/* Search */}
              <Input
                size="sm"
                placeholder="Search variables…"
                value={varSearch}
                onChange={e => setVarSearch(e.target.value)}
                borderRadius="8px"
                bg="white"
                fontSize="12px"
                _focus={{ borderColor: 'var(--brand-primary)', shadow: '0 0 0 2px rgba(1,172,251,0.15)' }}
              />

              {/* Categories */}
              {Object.entries(byCategory).map(([cat, vars]) => {
                if (vars.length === 0) return null
                const CatIcon = CATEGORY_ICONS[cat]
                const catColor = CATEGORY_COLORS[cat]
                return (
                  <Box key={cat}>
                    <Flex align="center" gap={1.5} mb={2}>
                      <CatIcon size={11} color={catColor} />
                      <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
                        {cat}
                      </Text>
                      <Badge
                        ml="auto"
                        fontSize="9px" px={1.5} py={0.5}
                        borderRadius="4px"
                        bg={`${catColor}15`}
                        color={catColor}
                        border={`1px solid ${catColor}30`}
                      >
                        {vars.length}
                      </Badge>
                    </Flex>
                    <VStack gap={1.5} align="stretch">
                      {vars.map(v => (
                        <VariableRow
                          key={v.id}
                          variable={v}
                          onInsert={() => handleInsertVariable(v.token)}
                          onCopy={() => handleCopyVariable(v.token)}
                        />
                      ))}
                    </VStack>
                  </Box>
                )
              })}

              {/* Custom variables section */}
              <Box>
                <Flex align="center" gap={1.5} mb={2}>
                  <Plus size={11} color="#7c3aed" />
                  <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
                    Custom
                  </Text>
                  {filteredCustom.length > 0 && (
                    <Badge
                      ml="auto"
                      fontSize="9px" px={1.5} py={0.5}
                      borderRadius="4px"
                      bg="#7c3aed15"
                      color="#7c3aed"
                      border="1px solid #7c3aed30"
                    >
                      {filteredCustom.length}
                    </Badge>
                  )}
                </Flex>

                {/* Add variable input */}
                <Flex gap={1.5} mb={2}>
                  <Input
                    size="sm"
                    placeholder="variable_name"
                    value={newVarName}
                    onChange={e => setNewVarName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddCustomVariable()}
                    borderRadius="7px"
                    fontSize="12px"
                    fontFamily="mono"
                    bg="white"
                    _focus={{ borderColor: '#7c3aed', shadow: '0 0 0 2px rgba(124,58,237,0.15)' }}
                  />
                  <Box
                    as="button"
                    flexShrink={0}
                    w="30px" h="32px"
                    borderRadius="7px"
                    bg="#7c3aed"
                    display="flex" alignItems="center" justifyContent="center"
                    cursor="pointer"
                    _hover={{ bg: '#6d28d9' }}
                    onClick={handleAddCustomVariable}
                  >
                    <Plus size={13} color="white" />
                  </Box>
                </Flex>

                {/* Custom variable list */}
                {filteredCustom.length > 0 && (
                  <VStack gap={1.5} align="stretch">
                    {filteredCustom.map(v => (
                      <VariableRow
                        key={v.id}
                        variable={v}
                        onInsert={() => handleInsertVariable(v.token)}
                        onCopy={() => handleCopyVariable(v.token)}
                        onDelete={() => removeCustomVariable(v.id)}
                      />
                    ))}
                  </VStack>
                )}

                {filteredCustom.length === 0 && !varSearch && (
                  <Text fontSize="11px" color="gray.400" fontStyle="italic">
                    No custom variables yet. Type a name and press Enter.
                  </Text>
                )}
              </Box>

              {filteredBuiltin.length === 0 && filteredCustom.length === 0 && varSearch && (
                <Box textAlign="center" py={8}>
                  <Text fontSize="12px" color="gray.400">No variables match "{varSearch}"</Text>
                </Box>
              )}

              {/* Helper tip */}
              <Box px={3} py={2.5} bg="blue.50" borderRadius="8px" border="1px solid" borderColor="blue.100">
                <Text fontSize="10.5px" color="blue.700" lineHeight="1.5" fontWeight="500">
                  Select a Text or Heading block, then click <strong>+</strong> to insert a variable.
                </Text>
              </Box>
            </VStack>
          </TabPanel>

          {/* Pre-built tab */}
          <TabPanel px={3} pt={3} pb={4} h="100%" overflowY="auto"
            css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' } }}
          >
            <PrebuiltPanel />
          </TabPanel>

          {/* Universal tab — reusable saved blocks */}
          <TabPanel px={3} pt={3} pb={4} h="100%" overflowY="auto"
            css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' } }}
          >
            <UniversalPanel />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}

export default BlocksSidebar
